import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'

import { useSelector } from 'react-redux'
import moment from 'moment'
import { useClient } from '../../graphql/provider'
import useGlobalStore from '../../stores/globalStore'
import { userInfo as selectUserInfo } from '../../redux/selectors/auth'

import {
  ApprovalsContainer,
  StyledPageContainer,
  StyledToggleButtonClear,
  StyledToggleContainerFullWidth,
  StyledToggleContainerPurple,
} from './Approvals.styles'
import ProjectApprovals from './ApprovalsView'
import useApprovalsData from './useApprovalsData'
import ProjectsView from './ProjecstView'
import { getProjectInvoices } from '../../entities/project/service'
import WorkInProgressPage from '../WorkInProgress/WorkInProgressPage'
import WipReportAiPage from '../WipReportAi/WipReportAiPage'
import MenuSection from '../../components/MenuSection'
import ExpensesPage from '../Expenses/ExpensesPage'
import { views } from './constants'
import InvoiceManagement from '../InvoiceManagment/InvoiceManagement'
import useFeatureFlags, { FeatureFlags } from '../../hooks/useFeatureFlags'
import ProjectOverviewPage from './ProjectOverview/ProjectOverviewPage'

const TAB_SLUGS = {
  'project-overview': views.projectOverview,
  approvals: views.approvals,
  expenses: views.expenses,
  projects: views.projects,
  wip: views.wip,
  'invoice-management': views.invoiceManagement,
}

const SLUG_BY_VIEW = Object.fromEntries(
  Object.entries(TAB_SLUGS).map(([slug, view]) => [view, slug]),
)

const useCalendarTime = ({ projects, timeEntries, rates }) => {
  const calendarValues = useMemo(() => {
    if (!projects || !timeEntries || !rates)
      return {
        current: [],
        prev: [],
      }

    const currentMonthStart = moment.utc().startOf('month')
    const currentMonthEnd = moment.utc().endOf('month')
    const prevMonthStart = currentMonthStart.clone().subtract(1, 'month')
    const prevMonthEnd = prevMonthStart.clone().endOf('month')

    const getValuesByMonth = (localEntries, localRates, monthStart, monthEnd) => {
      const currentMonthTotalDays = monthEnd.diff(monthStart, 'days') + 1

      const daysArray = Array.from({ length: currentMonthTotalDays }, (_, i) => i + 1)

      return projects.reduce((acc, project) => {
        const projectEntries = localEntries.filter(entry => entry.projectId === project._id)
        const projectRates = localRates[project._id]
        if (!projectRates || Object.values(projectRates).length === 0) return acc

        const projectValues = daysArray.map(day => {
          const currentDay = monthStart.clone().add(day - 1, 'days')

          const currentDayEntries = projectEntries.filter(entry =>
            moment(entry.entryDate).isSame(currentDay, 'day'),
          )

          const currentDayValue = currentDayEntries.reduce((sum, entry) => {
            return entry.durationInHours + sum
          }, 0)

          const currentDayRate = currentDayEntries.reduce((sum, entry) => {
            return projectRates[entry.personId] * entry.durationInHours + sum
          }, 0)

          return {
            value: currentDayValue,
            dayRate: currentDayRate,
          }
        })

        return {
          ...acc,
          [project._id]: projectValues,
        }
      }, {})
    }

    const current = getValuesByMonth(timeEntries, rates, currentMonthStart, currentMonthEnd)
    const prev = getValuesByMonth(timeEntries, rates, prevMonthStart, prevMonthEnd)

    return {
      current,
      prev,
    }
  }, [projects, timeEntries, rates])

  const calendarResume = useMemo(() => {
    const currentMonthRates = Object.entries(calendarValues.current).reduce(
      (acc, [projectId, values]) => {
        const projectTotalRate = values.reduce((sum, { dayRate }) => sum + dayRate, 0)
        const projectTotalHours = values.reduce((sum, { value }) => sum + value, 0)
        return {
          ...acc,
          [projectId]: { totalRate: projectTotalRate, totalHours: projectTotalHours },
        }
      },
      {},
    )

    const prevMonthRates = Object.entries(calendarValues.prev).reduce(
      (acc, [projectId, values]) => {
        const projectTotalRate = values.reduce((sum, { dayRate }) => sum + dayRate, 0)
        const projectTotalHours = values.reduce((sum, { value }) => sum + value, 0)
        return {
          ...acc,
          [projectId]: { totalRate: projectTotalRate, totalHours: projectTotalHours },
        }
      },
      {},
    )

    return {
      current: currentMonthRates,
      prev: prevMonthRates,
    }
  }, [calendarValues])

  return {
    calendarValues,
    calendarResume,
  }
}

const useInvoiceSummary = (client, projectId) => {
  const [invoices, setInvoices] = useState([])

  useEffect(() => {
    if (!projectId) return
    getProjectInvoices(client, projectId).then(result => {
      setInvoices(result)
    })
  }, [client, projectId])

  const updateInvoices = useCallback(
    (id, newInvoiceData) => {
      const updatedInvoices = invoices.map(invoice => {
        if (invoice._id === id) {
          return { ...invoice, ...newInvoiceData }
        }
        return invoice
      })
      setInvoices(updatedInvoices)
    },
    [invoices],
  )

  const invoiceSummary = useMemo(() => {
    if (!invoices || invoices.length === 0) {
      return { latest: null, totals: [] }
    }

    const sortedInvoices = [...invoices].sort((a, b) => new Date(b.due_date) - new Date(a.due_date))
    const latest = sortedInvoices[0]._id
    const totals = sortedInvoices.map(invoice => {
      const total = invoice.sections.reduce((sum, section) => sum + section.amount, 0)
      const month = moment(invoice.due_date).format('MMMM YYYY')
      return { total, month }
    })

    return { latest, totals, invoices }
  }, [invoices])

  return {
    invoiceSummary,
    actions: {
      updateInvoices,
    },
  }
}

const ApprovalsPage = () => {
  const client = useClient()
  const person = useSelector(selectUserInfo)
  const { selectedDelegateId } = useGlobalStore()
  const { tab } = useParams()
  const navigate = useNavigate()
  const view = TAB_SLUGS[tab]

  const setView = useCallback(
    newView => navigate(`../${SLUG_BY_VIEW[newView]}`, { replace: false }),
    [navigate],
  )
  const [selectedProject, setSelectedProject] = useState(null)

  const { isEnabled, isLoading } = useFeatureFlags()
  const isSuperUser = isEnabled(FeatureFlags.GLIMPSE_SUPER_USER) || false
  const isProjectOverviewEnabled = isEnabled(FeatureFlags.ENABLE_PROJECT_OVERVIEW_TAB)

  const { state, isLoadingProjectData, isProjectsLoaded, actions } = useApprovalsData({
    isLoadingFeatureFlags: isLoading,
    client,
    person,
    selectedDelegateId,
    isSuperUser,
    selectedProjectId: selectedProject?._id,
  })

  const {
    projects,
    timesheets,
    people,
    assignments,
    availableWeeks,
    entries,
    rates,
    expenses,
  } = state
  const { calendarValues, calendarResume } = useCalendarTime({
    projects: selectedProject ? [selectedProject] : [],
    timeEntries: entries,
    rates,
  })

  const {
    onEntryUpdate,
    onUpdateManyEntries,
    onReject,
    onSplitEntry,
    onApproveExpenseBillingStatus,
    onUpdateExpense,
    onSplitExpense,
  } = actions

  const {
    invoiceSummary: { totals: invoiceTotals, latest: latestInvoice, invoices },
    actions: { updateInvoices },
  } = useInvoiceSummary(client, selectedProject?._id)

  if (!view)
    return (
      <Navigate to={isProjectOverviewEnabled ? '../project-overview' : '../approvals'} replace />
    )

  return (
    <StyledPageContainer>
      <MenuSection
        person={person}
        selectedTab={view}
        setSelectedTab={setView}
        latestInvoice={latestInvoice}
        isLoadingProjects={!isProjectsLoaded}
        projects={projects}
        selectedProject={selectedProject}
        onProjectSelect={setSelectedProject}
        isProjectOverviewEnabled={isProjectOverviewEnabled}
      />
      {!isProjectsLoaded && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}>
          <CircularProgress />
        </div>
      )}
      {isProjectsLoaded && projects.length === 0 && (
        <div
          data-testid="dashboard.noProjects"
          style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}
        >
          <Typography variant="h6" style={{ color: '#666' }}>
            No projects available
          </Typography>
        </div>
      )}
      {isProjectsLoaded && projects.length > 0 && [views.approvals, views.projects].includes(view) && (
        <>
          <StyledToggleContainerFullWidth>
            <ToggleButtonGroup
              value={view}
              exclusive
              style={{ margin: '0 50px' }}
              onChange={(_, selectedView) => {
                if (!selectedView) return
                setView(selectedView)
              }}
            >
              <StyledToggleContainerPurple>
                <StyledToggleButtonClear
                  data-testid="dashboardViewToggle.week"
                  value={views.approvals}
                  onClick={() => setView(views.approvals)}
                  selected={view === views.approvals}
                >
                  Week View
                </StyledToggleButtonClear>
                <StyledToggleButtonClear
                  data-testid="dashboardViewToggle.month"
                  value={views.projects}
                  selected={view === views.projects}
                  onClick={() => setView(views.projects)}
                >
                  Month View
                </StyledToggleButtonClear>
              </StyledToggleContainerPurple>
            </ToggleButtonGroup>
          </StyledToggleContainerFullWidth>
          <ApprovalsContainer>
            {view === views.approvals && (
              <ProjectApprovals
                projects={projects}
                client={client}
                invoices={invoices}
                onEntryUpdate={onEntryUpdate}
                onUpdateManyEntries={onUpdateManyEntries}
                onReject={onReject}
                timeEntries={entries}
                availableWeeks={availableWeeks}
                people={people}
                timesheets={timesheets}
                person={person}
                rates={rates}
                expenses={expenses}
                calendarValues={calendarValues}
                calendarResume={calendarResume}
                onSplitEntry={onSplitEntry}
                onProjectSelect={setSelectedProject}
                selectedProject={selectedProject}
                isLoadingProjectData={isLoadingProjectData}
              />
            )}
            {view === views.projects && (
              <ProjectsView
                projects={projects}
                client={client}
                people={people}
                timesheets={timesheets}
                assignments={assignments}
                calendarValues={calendarValues}
                calendarResume={calendarResume}
                selectedProject={selectedProject}
                onProjectSelect={setSelectedProject}
              />
            )}
          </ApprovalsContainer>
        </>
      )}
      {isProjectsLoaded && projects.length > 0 && view === views.wip && (
        <WorkInProgressPage
          person={person}
          client={client}
          invoiceTotals={invoiceTotals}
          state={state}
          selectedProject={selectedProject}
          onProjectSelect={setSelectedProject}
        />
      )}
      {isProjectsLoaded && projects.length > 0 && view === views.wipAi && (
        <WipReportAiPage
          person={person}
          client={client}
          projects={projects}
          selectedProject={selectedProject}
          onProjectSelect={setSelectedProject}
          invoiceTotals={invoiceTotals}
        />
      )}
      {isProjectsLoaded && projects.length > 0 && view === views.expenses && (
        <ExpensesPage
          projects={projects}
          client={client}
          selectedProject={selectedProject}
          onEntryUpdate={onEntryUpdate}
          onUpdateManyEntries={onUpdateManyEntries}
          onReject={onReject}
          expenses={expenses}
          timeEntries={entries}
          availableWeeks={availableWeeks}
          people={people}
          timesheets={timesheets}
          person={person}
          rates={rates}
          calendarValues={calendarValues}
          calendarResume={calendarResume}
          onSplitEntry={onSplitEntry}
          onProjectSelect={setSelectedProject}
          onApproveExpenseBillingStatus={onApproveExpenseBillingStatus}
        />
      )}

      {isProjectsLoaded && projects.length > 0 && view === views.invoiceManagement && (
        <ApprovalsContainer>
          <InvoiceManagement
            onSplitExpense={onSplitExpense}
            updateInvoices={updateInvoices}
            projects={projects}
            invoices={invoices}
            client={client}
            selectedProject={selectedProject}
            onEntryUpdate={onEntryUpdate}
            onUpdateManyEntries={onUpdateManyEntries}
            onReject={onReject}
            expenses={expenses}
            timeEntries={entries}
            availableWeeks={availableWeeks}
            people={people}
            timesheets={timesheets}
            person={person}
            rates={rates}
            calendarValues={calendarValues}
            calendarResume={calendarResume}
            onSplitEntry={onSplitEntry}
            onProjectSelect={setSelectedProject}
            onApproveExpenseBillingStatus={onApproveExpenseBillingStatus}
            onUpdateExpense={onUpdateExpense}
          />
        </ApprovalsContainer>
      )}

      {isProjectOverviewEnabled && isProjectsLoaded && view === views.projectOverview ? (
        <ProjectOverviewPage selectedProject={selectedProject} client={client} />
      ) : null}
    </StyledPageContainer>
  )
}

export default ApprovalsPage
